import {
  CategoryFieldName,
  CategoryName,
  IDisplayModeEditableFieldMD,
  IInParamsValues,
  TDatabaseId,
  TSortQueryDirEnum,
  TSortQueryParams,
} from '@infotech-soe/metaschema';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import diff from 'lodash/difference';
import compose from 'lodash/fp/compose';
import isEmpty from 'lodash/isEmpty';

import { DEFAULT_PRESET, SEARCH_DIRECTIONS } from '../../constants/dataset';
import { DatasetGridViewModel } from '../../models/dataset/DatasetRelated';
import DisplayModeConfig, {
  DatasetColumnWidthConfig,
  DatasetConfig,
  IFrontDatasetPreset,
  TDatasetTotalsValues,
  TRecordViewMap,
} from '../../models/user-config/DisplayModeConfig';
import DatasetViewModel from '../../services/category/view-model/DatasetViewModel';
import { CommandsNameKeyMap } from '../../services/commands/constants';
import { RefreshStrategy } from '../../services/commands/wrap/CategoryCommand';
import {
  createDefaultPreset,
  recoverPreset,
} from '../../services/dataset/datasetConfigUtils';
import { CatalogContext } from '../../services/dataset/view-model/DatasetCurrentState';
import { DatasetGridColumn } from '../../services/dataset/view-model/DatasetGridColumn';
import { DatasetGridRow } from '../../services/dataset/view-model/DatasetGridRow';
import UniqueId from '../../services/uid/UniqueId';

import {
  emptyAction,
  failureAction,
  pendingAction,
  successAction,
} from './actionHandler';
import { CategoryTargetingInfo } from './category';

export const enum DatasetSwitchEffect {
  REVIEW_FORM = 'reviewForm',
  WORK_TIMES = 'workTimes',
  NOTHING = 'nothing',
}

export type DatasetState = Record<UniqueId, DatasetGridViewModel>;

const updateActiveRowId = (
  prevState: DatasetGridViewModel,
  newRows: DatasetGridRow[]
): TDatabaseId | null => {
  if (prevState.activeRowId === null) {
    return null;
  }

  const exist = newRows.some(
    row => row[prevState.rowIdField] === prevState.activeRowId
  );

  return exist ? prevState.activeRowId : null;
};

const datasetSlice = createSlice({
  name: 'dataset',
  initialState: {} as DatasetState,
  reducers: {
    create: pendingAction<
      DatasetState,
      {
        categoryId: UniqueId;
        datasetId: UniqueId;
        datasetConfig?: DatasetConfig;
      }
    >(),
    createSuccess: successAction<
      DatasetState,
      {
        categoryId: UniqueId;
        datasetId: UniqueId;
        columns: DatasetGridColumn[];
        rowIdField: CategoryFieldName;
        treeField?: CategoryFieldName;
        config: DatasetConfig;
        source: { category: CategoryName };
        editableFields?: Record<CategoryFieldName, IDisplayModeEditableFieldMD>;
        hideCheckbox?: boolean;
      }
    >(
      (
        state,
        {
          payload: {
            categoryId,
            datasetId,
            columns,
            rowIdField,
            treeField,
            config,
            source,
            editableFields,
            hideCheckbox,
          },
        }
      ) => {
        const recoverEveryPreset = compose(
          Object.fromEntries,
          (presets: Array<[string, IFrontDatasetPreset]>) =>
            presets.map(([name, preset]) => [
              name,
              recoverPreset(preset, columns),
            ]),
          Object.entries
        );

        const recoverColumnsOrder = (
          columnsList: string[],
          order: string[]
        ): string[] => [
          ...order.filter(column => columnsList.includes(column)),
          ...diff(columnsList, order),
        ];

        const recoverColumnsWidth = (columnsList: string[]): any =>
          compose(
            Object.fromEntries,
            (width: Array<[string, number]>) =>
              width.filter(([column]) => columnsList.includes(column)),
            Object.entries
          );

        state[datasetId] = {
          id: datasetId,
          categoryId,
          columns,
          treeField,
          rowIdField,
          rowsFullCount: 0,
          rows: [],
          activeRowId: null,
          catalogContext: null,
          outdated: true,
          config: {
            ...config,
            columnsWidth: recoverColumnsWidth(
              columns.map(({ field }) => field)
            )(config.columnsWidth),
            columnsOrder: recoverColumnsOrder(
              columns.map(({ field }) => field),
              config.columnsOrder
            ),
            preset: isEmpty(config.preset)
              ? { [DEFAULT_PRESET]: createDefaultPreset(columns) }
              : recoverEveryPreset(config.preset),
          },
          source,
          selectedIds: [],
          searchQuery: '',
          editableFields,
          hideCheckbox,
        };
      }
    ),
    createFailure: failureAction(),
    createBatch: pendingAction<
      DatasetState,
      {
        categoryId: UniqueId;
        datasets: Record<UniqueId, DatasetViewModel>;
        displayModeConfig?: DisplayModeConfig;
        displayMode?: string;
        inParams?: IInParamsValues;
        pinned?: boolean;
      }
    >(),
    createBatchSuccess: successAction<
      DatasetState,
      {
        categoryId: UniqueId;
        displayMode?: string;
        inParams?: IInParamsValues;
        treeActiveId?: TDatabaseId | null;
        treeCheckedIds?: TDatabaseId[];
        pinned?: boolean;
      }
    >(),
    createBatchFailure: failureAction(),
    addRows: pendingAction<
      DatasetState,
      { datasetId: UniqueId; ids: TDatabaseId[]; formId?: UniqueId }
    >(),
    removeRows: pendingAction<
      DatasetState,
      { datasetId: UniqueId; ids: TDatabaseId[] }
    >(),
    update: pendingAction<
      DatasetState,
      {
        datasetId: UniqueId;
        targetingInfo?: CategoryTargetingInfo;
        formId?: UniqueId;
        forceDOMFocus?: true;
      }
    >((state, { payload: { datasetId } }) => {
      state[datasetId].selectedIds = [];
    }),
    updateSuccess: successAction<
      DatasetState,
      {
        datasetId: UniqueId;
        targetingInfo?: CategoryTargetingInfo;
        formId?: UniqueId;
        forceDOMFocus?: true;
        rows: DatasetGridRow[];
        rowsFullCount: number;
        activeRowId?: TDatabaseId;
        totals?: TDatasetTotalsValues;
        header?: string;
        totalsInvalid?: boolean;
      }
    >(
      (
        state,
        {
          payload: {
            datasetId,
            rows,
            rowsFullCount,
            activeRowId,
            totals,
            header,
            totalsInvalid,
          },
        }
      ) => {
        const dataset = state[datasetId];

        dataset.rows = rows;
        dataset.rowsFullCount = rowsFullCount;
        dataset.activeRowId = activeRowId ?? updateActiveRowId(dataset, rows);
        dataset.outdated = false;
        dataset.totals = totals;
        dataset.header = header;
        dataset.totalsInvalid = !!totalsInvalid;
      }
    ),
    updateFailure: failureAction(),
    updateRows: pendingAction<
      DatasetState,
      { datasetId: UniqueId; ids: TDatabaseId[]; formId?: UniqueId }
    >((state, { payload: { datasetId } }) => {
      state[datasetId].selectedIds = [];
    }),
    sort: pendingAction<
      DatasetState,
      { datasetId: UniqueId; field: string; multiSort?: boolean }
    >((state, { payload: { datasetId, field, multiSort } }) => {
      const createSortField = (sortField: string): TSortQueryParams => ({
        field: sortField,
        dir: TSortQueryDirEnum.NO,
      });

      const nextSort = (dir: TSortQueryDirEnum): TSortQueryDirEnum => {
        const order = [
          TSortQueryDirEnum.ASC,
          TSortQueryDirEnum.DESC,
          TSortQueryDirEnum.NO,
        ];
        const index = order.indexOf(dir);

        return order[(index + 1) % order.length];
      };

      const { sort } = state[datasetId].config;
      const prevIndex = sort.findIndex(
        ({ field: cField }: TSortQueryParams) => field === cField
      );
      const prevSort = prevIndex < 0 ? createSortField(field) : sort[prevIndex];

      const nextSortDir = nextSort(prevSort.dir);

      if (!multiSort) {
        const sortDef = sort.filter((param: TSortQueryParams) => param.default);
        state[datasetId].config.sort = sortDef.concat(
          nextSortDir !== TSortQueryDirEnum.NO
            ? [
                {
                  ...prevSort,
                  dir: nextSortDir,
                },
              ]
            : []
        );
      } else if (prevSort.dir === TSortQueryDirEnum.NO) {
        sort.push({
          ...prevSort,
          dir: nextSortDir,
        });
      } else if (nextSortDir === TSortQueryDirEnum.NO) {
        sort.splice(prevIndex, 1);
      } else {
        sort[prevIndex].dir = nextSortDir;
      }
    }),
    sortSuccess: successAction<
      DatasetState,
      { datasetId: UniqueId; rows: DatasetGridRow[]; rowsFullCount: number }
    >((state, { payload: { datasetId, rows, rowsFullCount } }) => {
      const dataset = state[datasetId];

      dataset.rows = rows;
      dataset.rowsFullCount = rowsFullCount;
      dataset.activeRowId = updateActiveRowId(dataset, rows);
      dataset.outdated = false;
    }),
    sortFailure: failureAction(),
    dispose(
      state,
      { payload: { datasetId } }: PayloadAction<{ datasetId: UniqueId }>
    ) {
      delete state[datasetId];
    },
    findRecord(
      state,
      {
        payload: { datasetId, query, column, dir },
      }: PayloadAction<{
        datasetId: UniqueId;
        query: string;
        column: string;
        dir: SEARCH_DIRECTIONS;
      }>
    ) {
      const dataset = state[datasetId];

      dataset.searchQuery = query;

      const currentRowIndex = dataset.rows.findIndex(
        row => row[dataset.rowIdField] === dataset.activeRowId
      );

      let rows: any[] = [];

      switch (dir) {
        case SEARCH_DIRECTIONS.PREV:
          rows = dataset.rows.slice(0, currentRowIndex - 1).reverse();
          break;
        case SEARCH_DIRECTIONS.NEXT:
          rows = dataset.rows.slice(currentRowIndex + 1);
          break;
        default:
          rows = dataset.rows;
      }

      const queryInLowRegister = query.toLowerCase();
      const record = rows.find(row =>
        String(row[column]).toLowerCase().includes(queryInLowRegister)
      );

      if (record?.[dataset.rowIdField]) {
        dataset.activeRowId = record[dataset.rowIdField];
      }
    },
    changeActiveRow(
      state,
      {
        payload: { datasetId, rowId },
      }: PayloadAction<{
        datasetId: UniqueId;
        rowId: TDatabaseId;
        effect: DatasetSwitchEffect;
      }>
    ) {
      state[datasetId].activeRowId = rowId;
    },
    changeSelectedIds(
      state,
      {
        payload: { datasetId, selectedIds },
      }: PayloadAction<{
        datasetId: UniqueId;
        selectedIds: TDatabaseId[];
      }>
    ) {
      state[datasetId].selectedIds = selectedIds;
    },
    selectPreviousRecord: emptyAction<{
      datasetId: UniqueId;
      effect: DatasetSwitchEffect;
    }>(),
    selectNextRecord: emptyAction<{
      datasetId: UniqueId;
      effect: DatasetSwitchEffect;
    }>(),
    setCatalogContext(
      state,
      {
        payload: { datasetId, catalogContext },
      }: PayloadAction<{
        datasetId: UniqueId;
        catalogContext: CatalogContext;
      }>
    ) {
      state[datasetId].catalogContext = catalogContext;
    },
    updateCatalogContext: emptyAction<{ datasetId: UniqueId }>(),
    openConfigForm(
      state,
      { payload: { datasetId } }: PayloadAction<{ datasetId: UniqueId }>
    ) {
      state[datasetId].config.showForm = true;
    },
    closeConfigForm(
      state,
      { payload: { datasetId } }: PayloadAction<{ datasetId: UniqueId }>
    ) {
      state[datasetId].config.showForm = false;
    },
    updateConfig(
      state,
      {
        payload: { datasetId, config },
      }: PayloadAction<{
        datasetId: UniqueId;
        config: DatasetConfig;
        needToUpdate?: boolean;
      }>
    ) {
      state[datasetId].config = config;
    },
    invalidate(
      state,
      { payload: { datasetId } }: PayloadAction<{ datasetId: UniqueId }>
    ) {
      state[datasetId] = {
        ...state[datasetId],
        rows: [],
        activeRowId: null,
        outdated: true,
        selectedIds: [],
      };
    },
    resizeColumn(
      state,
      {
        payload: { datasetId, config },
      }: PayloadAction<{
        datasetId: UniqueId;
        config: DatasetColumnWidthConfig;
      }>
    ) {
      state[datasetId].config.columnsWidth[config.field] = config.width;
    },
    moveColumn(
      state,
      {
        payload: { datasetId, order },
      }: PayloadAction<{
        datasetId: UniqueId;
        order: string[];
      }>
    ) {
      state[datasetId].config.columnsOrder = order;
    },
    refreshWithStrategy: emptyAction<{
      datasetId: UniqueId;
      strategy?: RefreshStrategy;
      ids: TDatabaseId[];
    }>(),
    updateRecordViewMap: pendingAction<DatasetState, { datasetId: UniqueId }>(),
    updateRecordViewMapSuccess: successAction<
      DatasetState,
      { datasetId: UniqueId; recordViewMap?: TRecordViewMap }
    >((state, { payload: { datasetId, recordViewMap } }) => {
      state[datasetId].recordViewMap = recordViewMap;
    }),
    updateRecordViewMapFailure: failureAction(),
    updateKeyMap(
      state,
      {
        payload: { datasetId, keyMap },
      }: PayloadAction<{
        datasetId: UniqueId;
        keyMap: CommandsNameKeyMap;
      }>
    ) {
      state[datasetId].keyMap = keyMap;
    },
  },
});

export const datasetReducer = datasetSlice.reducer;

export default datasetSlice.actions;
